/**
 * Faixas do histograma de latência por token.
 *
 * O histograma existe para tornar a **cauda à direita** visível: é ela que separa a
 * mediana do percentil 95, e é essa separação que justifica a regra de nunca apresentar
 * média. Extraído da tela para que a divisão em faixas seja verificada por teste.
 */

import type { HistogramBar } from "./chart-options.js";

/** Número máximo de faixas; reduzido quando há poucas amostras. */
export const MAX_HISTOGRAM_BINS = 24;

/** Número mínimo de faixas, para a distribuição não virar uma barra só. */
export const MIN_HISTOGRAM_BINS = 6;

/** Amostras mínimas por faixa, para o histograma não virar um pente. */
export const MIN_SAMPLES_PER_BIN = 4;

/**
 * Divide os valores em faixas de largura uniforme e classifica cada faixa frente ao alvo.
 *
 * Faixa inteira abaixo do alvo conta como atendida; faixa que o cruza, não — o critério
 * é conservador de propósito, porque a tela decide compra.
 *
 * Valores todos iguais dariam largura zero e divisão por zero; a largura cai para 1 e
 * tudo vai para a primeira faixa, em vez de produzir `NaN` silencioso no gráfico.
 */
export function buildHistogram(values: readonly number[], target: number): HistogramBar[] {
  if (values.length === 0) {
    return [];
  }
  const binCount = Math.max(
    MIN_HISTOGRAM_BINS,
    Math.min(MAX_HISTOGRAM_BINS, Math.floor(values.length / MIN_SAMPLES_PER_BIN)),
  );
  const min = Math.min(...values);
  const max = Math.max(...values);
  const width = (max - min) / binCount || 1;
  // O último valor cai exatamente na borda direita e daria índice `binCount`; é puxado
  // para a última faixa em vez de criar uma faixa a mais.
  const indices = values.map((value) => Math.min(binCount - 1, Math.floor((value - min) / width)));
  return Array.from({ length: binCount }, (_, index) => {
    const start = min + index * width;
    const count = indices.filter((bin) => bin === index).length;
    return { bin: start.toFixed(1), count, withinTarget: start + width <= target };
  });
}
